import { create } from 'zustand';
import { notesDB, deletedItemsDB, type Note } from '../../lib/db';
import { extractPlainText } from '../../lib/note-utils';
import type { JSONContent } from '@tiptap/core';

type SortOption = 'updated' | 'created' | 'title' | 'category';

interface NotesState {
  // State
  notes: Note[];
  selectedNoteId: string | null;
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
  currentSpaceId: string;
  categoryFilter: string | null; // null = all categories
  sortBy: SortOption;

  // Actions
  loadNotes: (spaceId: string) => Promise<void>;
  selectNote: (noteId: string | null) => void;
  createNote: (spaceId: string, userId: string) => Promise<Note>;
  updateNote: (noteId: string, updates: Partial<Note>) => Promise<void>;
  deleteNote: (noteId: string) => Promise<void>;
  setSearchQuery: (query: string) => void;
  setCurrentSpace: (spaceId: string) => void;
  setCategoryFilter: (categoryId: string | null) => void;
  setSortBy: (sortBy: SortOption) => void;
  togglePinNote: (noteId: string) => Promise<void>;
  toggleArchiveNote: (noteId: string) => Promise<void>;

  // Computed
  getSelectedNote: () => Note | undefined;
  getFilteredNotes: () => Note[];
  getPinnedNotes: () => Note[];
}

export const useNotesStore = create<NotesState>((set, get) => ({
  // Initial state
  notes: [],
  selectedNoteId: null,
  isLoading: false,
  error: null,
  searchQuery: '',
  currentSpaceId: 'default',
  categoryFilter: null,
  sortBy: 'updated',

  // Load notes for a space
  loadNotes: async (spaceId: string) => {
    try {
      set({ isLoading: true, error: null });
      const notes = await notesDB.getBySpace(spaceId);

      // Get currently selected note ID to avoid deleting it
      const { selectedNoteId } = get();

      // Clean up any empty "Untitled Note" entries on load
      // BUT skip the currently selected note (user might be working on it)
      const emptyNotes = notes.filter(note => {
        if (note.id === selectedNoteId) return false; // Don't delete selected note
        if (note.title !== 'Untitled Note') return false; // Keep notes with custom titles

        // Check if content is empty based on format
        const plainText = typeof note.content === 'string'
          ? note.content
          : extractPlainText(note.content as JSONContent);

        return plainText.trim() === '';
      });

      // Delete empty notes in the background (except the selected one)
      if (emptyNotes.length > 0) {
        Promise.all(
          emptyNotes.map(note => notesDB.delete(note.id))
        ).catch(err => console.error('Failed to clean up empty notes:', err));

        // Filter out empty notes from the loaded notes (except the selected one)
        const filteredNotes = notes.filter(note => {
          if (note.id === selectedNoteId) return true; // Keep selected note
          if (note.title !== 'Untitled Note') return true; // Keep if has custom title

          // Check if content is empty based on format
          const isEmpty = note.contentFormat === 'tiptap-json'
            ? extractPlainText(note.content as JSONContent).trim() === ''
            : (typeof note.content === 'string' && note.content.trim() === '');

          return !isEmpty; // Filter out empty notes
        });
        set({ notes: filteredNotes, isLoading: false, currentSpaceId: spaceId });
      } else {
        set({ notes, isLoading: false, currentSpaceId: spaceId });
      }
    } catch (error) {
      console.error('Failed to load notes:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to load notes',
        isLoading: false,
      });
    }
  },

  // Select a note
  selectNote: (noteId: string | null) => {
    set({ selectedNoteId: noteId });
  },

  // Create a new note
  createNote: async (spaceId: string, userId: string) => {
    try {
      set({ isLoading: true, error: null });

      const newNote = await notesDB.create({
        spaceId,
        title: 'Untitled Note',
        content: '',
        tags: [],
        isPinned: false,
        isArchived: false,
        createdBy: userId,
        lastEditedBy: userId,
        syncStatus: 'pending',
      });

      set((state) => ({
        notes: [newNote, ...state.notes],
        selectedNoteId: newNote.id,
        isLoading: false,
      }));

      return newNote;
    } catch (error) {
      console.error('Failed to create note:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to create note',
        isLoading: false,
      });
      throw error;
    }
  },

  // Update a note
  updateNote: async (noteId: string, updates: Partial<Note>) => {
    try {
      const updatedNote = await notesDB.update(noteId, {
        ...updates,
        syncStatus: 'pending', // Mark for sync
      });

      set((state) => ({
        notes: state.notes.map((note) => (note.id === noteId ? updatedNote : note)),
      }));
    } catch (error) {
      console.error('Failed to update note:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to update note',
      });
      throw error;
    }
  },

  // Delete a note
  deleteNote: async (noteId: string) => {
    try {
      // Get the note to check if it has a driveFileId
      const note = get().notes.find((n) => n.id === noteId);

      // Delete from IndexedDB
      await notesDB.delete(noteId);

      // Track deletion to prevent re-download from Drive
      await deletedItemsDB.add(noteId, 'note');

      // If note was synced to Drive, delete from Drive too
      if (note?.driveFileId) {
        try {
          const { defaultSyncEngine } = await import('../../lib/sync-engine');
          await defaultSyncEngine.deleteNoteFromDrive(note.driveFileId);
        } catch (driveError) {
          console.error('Failed to delete from Google Drive:', driveError);
          // Continue anyway - local deletion succeeded and tracked
        }
      }

      set((state) => ({
        notes: state.notes.filter((note) => note.id !== noteId),
        selectedNoteId: state.selectedNoteId === noteId ? null : state.selectedNoteId,
      }));
    } catch (error) {
      console.error('Failed to delete note:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to delete note',
      });
      throw error;
    }
  },

  // Set search query
  setSearchQuery: (query: string) => {
    set({ searchQuery: query });
  },

  // Set category filter
  setCategoryFilter: (categoryId: string | null) => {
    set({ categoryFilter: categoryId });
  },

  // Set sort option
  setSortBy: (sortBy: SortOption) => {
    set({ sortBy });
  },

  // Set current space (NotesList handles loadNotes via its own useEffect)
  setCurrentSpace: (spaceId: string) => {
    set({ currentSpaceId: spaceId });
  },

  // Toggle pin status
  togglePinNote: async (noteId: string) => {
    const note = get().notes.find((n) => n.id === noteId);
    if (!note) return;

    await get().updateNote(noteId, { isPinned: !note.isPinned });
  },

  // Toggle archive status
  toggleArchiveNote: async (noteId: string) => {
    const note = get().notes.find((n) => n.id === noteId);
    if (!note) return;

    await get().updateNote(noteId, { isArchived: !note.isArchived });
  },

  // Get selected note
  getSelectedNote: () => {
    const { notes, selectedNoteId } = get();
    return notes.find((note) => note.id === selectedNoteId);
  },

  // Get filtered notes (search + category + not archived)
  getFilteredNotes: () => {
    const { notes, searchQuery, categoryFilter, sortBy } = get();

    let filtered = notes.filter((note) => !note.isArchived);

    // Filter by category
    if (categoryFilter) {
      if (categoryFilter === '__NO_CATEGORY__') {
        // Filter for notes without a category
        filtered = filtered.filter((note) => !note.category || note.category === '');
      } else {
        // Filter for specific category
        filtered = filtered.filter((note) => note.category === categoryFilter);
      }
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((note) => {
        // Extract searchable content based on format
        const searchableContent = note.contentFormat === 'tiptap-json'
          ? extractPlainText(note.content as JSONContent)
          : (note.content as string);

        return (
          note.title.toLowerCase().includes(query) ||
          searchableContent.toLowerCase().includes(query) ||
          note.tags.some((tag) => tag.toLowerCase().includes(query))
        );
      });
    }

    // Sort: pinned first, then by selected sort option
    return filtered.sort((a, b) => {
      // Always prioritize pinned notes
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;

      // Then sort by selected option
      switch (sortBy) {
        case 'updated': {
          const timeDiff = new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
          // If updatedAt is the same, use createdAt as secondary sort (stable sort)
          if (timeDiff === 0) {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          }
          return timeDiff;
        }
        case 'created': {
          const timeDiff = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          // If createdAt is the same, use id as secondary sort (stable sort)
          if (timeDiff === 0) {
            return a.id.localeCompare(b.id);
          }
          return timeDiff;
        }
        case 'title':
          // If titles are the same, use createdAt as secondary sort
          const titleComp = a.title.localeCompare(b.title);
          if (titleComp === 0) {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          }
          return titleComp;
        case 'category': {
          // Sort by category (notes with categories first, then alphabetically by category)
          if (!a.category && b.category) return 1;
          if (a.category && !b.category) return -1;
          if (!a.category && !b.category) {
            // Both have no category, sort by createdAt
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          }
          const catComp = (a.category || '').localeCompare(b.category || '');
          // If categories are the same, use createdAt as secondary sort
          if (catComp === 0) {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          }
          return catComp;
        }
        default: {
          const timeDiff = new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
          if (timeDiff === 0) {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          }
          return timeDiff;
        }
      }
    });
  },

  // Get pinned notes
  getPinnedNotes: () => {
    const { notes } = get();
    return notes.filter((note) => note.isPinned && !note.isArchived);
  },
}));
